import {
  Card,
  CardBody,
  Avatar,
  Spinner,
  RadioGroup,
  Radio,
} from '@heroui/react';
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { FiPlus } from 'react-icons/fi';

import { useDebounce } from '../../../hooks';
import { AlertCard } from '../../AlertCard';
import { BaseModal } from '../../BaseModal';
import { SearchInput } from '../SearchInput';

export interface EntitySearchOption {
  id: string;
  name: string;
  label: string;
  avatar?: string;
  subtitle?: string;
  metadata?: {
    // User metadata
    email?: string;
    phone?: string;
    whatsApp?: boolean;
    sms?: boolean;
    fax?: boolean;
    orderPreferences?: Array<{ type: string }>;
    // Buyer metadata
    rolesEnabled?: boolean;
    fullAddress?: string;
    customerNumberMandatory?: boolean;
  };
  /** Set when this option can be found but not added; shown before confirming. */
  blocked?: {
    title: string;
    message: string;
  };
}

export interface RoleOption {
  value: string;
  label: string;
  description?: string;
}

export interface CreateNewOption {
  label: string;
  description: string;
  onNavigate: () => void;
}

export interface EntitySearchModalProps {
  // Modal state
  isOpen: boolean;
  onClose: () => void;

  // Content
  title: string;
  searchPlaceholder: string;
  entityType: string; // "user", "buyer", "supplier"

  // Search functionality
  isSearching: boolean;
  searchResults: EntitySearchOption[];
  onSearch: (searchTerm: string) => void;

  // Selection and confirmation
  onConfirm: (selected: EntitySearchOption, role?: string) => Promise<void>;
  isConfirming?: boolean;

  // Selection handling
  onSelect?: (selected: EntitySearchOption) => void;

  // Role selection (optional)
  roleOptions?: RoleOption[];
  defaultRole?: string;

  // Create new option (optional)
  createNewOption?: CreateNewOption;

  // Customization
  renderEntity?: (
    entity: EntitySearchOption,
    onSelect: (entity: EntitySearchOption) => void
  ) => React.ReactNode;
  renderConfirmation?: (
    entity: EntitySearchOption,
    role?: string
  ) => React.ReactNode;

  // Empty state
  emptyMessage?: string;

  // Optional control rendered directly under the search input (e.g. a scope
  // toggle). Only shown during the search phase.
  filterControl?: React.ReactNode;

  // Set when the caller hands over a complete list instead of server-side
  // search results, so the search box narrows what is already loaded.
  filterResultsLocally?: boolean;

  // Error handling
  error?: string | null;
  onErrorDismiss?: () => void;
}

export const EntitySearchModal: React.FC<EntitySearchModalProps> = ({
  onSelect,
  isOpen,
  onClose,
  title,
  searchPlaceholder,
  entityType,
  isSearching,
  searchResults,
  onSearch,
  onConfirm,
  isConfirming = false,
  roleOptions,
  defaultRole,
  createNewOption,
  renderEntity,
  renderConfirmation,
  emptyMessage,
  filterControl,
  filterResultsLocally = false,
  error,
  onErrorDismiss,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEntity, setSelectedEntity] =
    useState<EntitySearchOption | null>(null);
  const [selectedRole, setSelectedRole] = useState(defaultRole || '');
  const [localError, setLocalError] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const visibleResults = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();
    if (!filterResultsLocally || !term) return searchResults;

    return searchResults.filter((entity) =>
      [
        entity.name,
        entity.label,
        entity.subtitle,
        entity.metadata?.email,
        entity.metadata?.phone,
      ].some((field) => field?.toLowerCase().includes(term))
    );
  }, [filterResultsLocally, searchResults, debouncedSearchTerm]);

  // Trigger search when debounced search term changes
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      onSearch(debouncedSearchTerm.trim());
    }
  }, [debouncedSearchTerm, onSearch]);

  // Set default role when role options change
  useEffect(() => {
    if (roleOptions && roleOptions.length > 0 && !selectedRole) {
      setSelectedRole(defaultRole || roleOptions[0].value);
    }
  }, [roleOptions, defaultRole, selectedRole]);

  const handleClose = () => {
    setSearchTerm('');
    setSelectedEntity(null);
    setSelectedRole(defaultRole || '');
    setLocalError(null);
    onErrorDismiss?.();
    onClose();
  };

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setLocalError(null);
      onErrorDismiss?.();
    },
    [onErrorDismiss]
  );

  const handleEntitySelect = (entity: EntitySearchOption) => {
    setSelectedEntity(entity);
    setLocalError(null);
    onErrorDismiss?.();
    if (onSelect) {
      onSelect(entity);
    }
  };

  const handleConfirm = async () => {
    if (!selectedEntity) return;

    try {
      setLocalError(null);
      await onConfirm(selectedEntity, selectedRole || undefined);
      handleClose();
    } catch (confirmError) {
      const errorMessage =
        confirmError instanceof Error
          ? confirmError.message
          : `Failed to add ${entityType}`;
      setLocalError(errorMessage);
    }
  };

  const handleGoBack = () => {
    setSelectedEntity(null);
    setLocalError(null);
    onErrorDismiss?.();
  };

  const displayError = error || localError;

  const defaultRenderEntity = (entity: EntitySearchOption) => (
    // A plain button, not a HeroUI Button: the latter centers its content, so
    // the name floated to the middle of the row while the avatar pinned left.
    // Content is left-aligned and long values (e.g. UUID-style emails) truncate.
    <button
      key={entity.id}
      type="button"
      onClick={() => handleEntitySelect(entity)}
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:bg-default-100"
    >
      <Avatar
        name={entity.name?.charAt(0) || entity.id.charAt(0)}
        size="sm"
        color="primary"
        src={entity.avatar}
        className="shrink-0"
      />
      <div className="min-w-0 flex-1">
        <p className="font-medium text-foreground truncate">{entity.name}</p>
        {entity.subtitle && (
          <p className="text-xs text-default-500 truncate">{entity.subtitle}</p>
        )}
        {entity.blocked && (
          <p className="text-xs text-warning-600 truncate">
            {entity.blocked.title}
          </p>
        )}
      </div>
    </button>
  );

  const defaultRenderConfirmation = (
    entity: EntitySearchOption,
    role?: string
  ) => (
    <div className="space-y-4">
      <div className="p-4 bg-primary-50 rounded-lg">
        <div className="flex items-center gap-3">
          <Avatar
            name={entity.name?.charAt(0) || entity.id.charAt(0)}
            size="md"
            color="primary"
            src={entity.avatar}
          />
          <div>
            <p className="font-semibold text-foreground">{entity.name}</p>
            {entity.subtitle && (
              <p className="text-sm text-default-500">{entity.subtitle}</p>
            )}
          </div>
        </div>
      </div>

      {role && (
        <div className="p-3 bg-success-50 rounded-lg">
          <p className="text-sm text-success-800">
            <strong>Role:</strong>{' '}
            {roleOptions?.find((r) => r.value === role)?.label || role}
          </p>
        </div>
      )}
    </div>
  );

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={title}
      subtitle={
        !selectedEntity
          ? `Search for existing ${entityType}s${
              createNewOption ? ' or create a new one' : ''
            }`
          : undefined
      }
      footerContent={{
        onCancel: selectedEntity ? handleGoBack : handleClose,
        onConfirm: selectedEntity ? handleConfirm : undefined,
        isLoading: isConfirming,
        isDisabled:
          !!selectedEntity?.blocked ||
          (roleOptions && roleOptions.length > 0 && !selectedRole),
        cancelText: selectedEntity ? 'Back' : 'Cancel',
        confirmText: isConfirming ? 'Adding...' : `Add ${entityType}`,
      }}
    >
      {displayError && (
        <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg mb-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 bg-danger-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">!</span>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-danger-800 mb-1">Error</p>
              <p className="text-sm text-danger-700">{displayError}</p>
            </div>
          </div>
        </div>
      )}

      {selectedEntity ? (
        // Confirmation Phase
        <div className="space-y-4">
          {renderConfirmation
            ? renderConfirmation(selectedEntity, selectedRole)
            : defaultRenderConfirmation(selectedEntity, selectedRole)}

          {selectedEntity.blocked && (
            <AlertCard
              type="warning"
              title={selectedEntity.blocked.title}
              message={selectedEntity.blocked.message}
            />
          )}

          {roleOptions && roleOptions.length > 0 && !selectedEntity.blocked && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">
                Select Role *
              </label>
              <RadioGroup
                value={selectedRole}
                onValueChange={setSelectedRole}
                orientation="vertical"
              >
                {roleOptions.map((role) => (
                  <Radio
                    key={role.value}
                    value={role.value}
                    classNames={{
                      base: 'data-[selected=true]:bg-primary-50 rounded-lg p-3 transition-colors',
                      wrapper: 'hidden',
                      control: 'hidden',
                      hiddenInput: 'absolute opacity-0',
                      labelWrapper: 'w-full',
                    }}
                  >
                    <div>
                      <p className="font-medium">{role.label}</p>
                      {role.description && (
                        <p className="text-xs text-default-500">
                          {role.description}
                        </p>
                      )}
                    </div>
                  </Radio>
                ))}
              </RadioGroup>
            </div>
          )}
        </div>
      ) : (
        // Search Phase
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Search {entityType}s
            </label>
            <SearchInput
              value={searchTerm}
              placeholder={searchPlaceholder}
              onChange={handleSearchChange}
              ariaLabel={`Search ${entityType}s`}
              size="md"
            />
            {filterControl}
          </div>

          {/* Create new option - always visible */}
          {createNewOption && (
            <Card className="mb-4">
              <CardBody className="p-0">
                <div
                  className="flex items-center gap-3 p-3 hover:bg-default-100 cursor-pointer"
                  onClick={() => {
                    handleClose();
                    createNewOption.onNavigate();
                  }}
                >
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                    <FiPlus size={16} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-primary">
                      {createNewOption.label}
                    </p>
                    <p className="text-xs text-default-500">
                      {createNewOption.description}
                    </p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}

          {/* Search results */}
          <Card className="border border-default-200">
            <CardBody className="p-0 max-h-[320px] overflow-y-auto">
              {isSearching ? (
                <div className="flex justify-center py-6">
                  <Spinner size="sm" />
                </div>
              ) : (
                <>
                  {visibleResults.map((entity) => (
                    <div
                      key={entity.id}
                      className="border-b border-default-200 last:border-b-0"
                    >
                      {renderEntity
                        ? renderEntity(entity, handleEntitySelect)
                        : defaultRenderEntity(entity)}
                    </div>
                  ))}

                  {visibleResults.length === 0 && !isSearching && (
                    <div className="py-6 text-center text-default-500">
                      <p className="font-medium">
                        {emptyMessage || `No ${entityType}s found`}
                      </p>
                      {!emptyMessage && (
                        <p className="text-sm mt-1">
                          Try a different search term
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </BaseModal>
  );
};
